
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import SectionCard from "../ui/SectionCard";
import Button from "../../../components/Button/Button.jsx";

import {
  getExpenseClaimById,
  submitExpenseClaim,
} from "../../../services/expenseServices";

import {
  FaClipboardCheck,
  FaSyncAlt,
  FaPaperPlane,
  FaRoute,
  FaArrowRight,
  FaCar,
} from "react-icons/fa";

/* ==========================================================================
   HELPERS
   ========================================================================== */

const num = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const n = Number(String(value).replace(",", "."));

  return Number.isFinite(n) ? n : 0;
};

const money = (value) =>
  num(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const kmLabel = (value) =>
  num(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });

const rateLabel = (value) =>
  num(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

const badge = (statut) => {
  const s = String(statut || "draft");

  if (s === "draft") {
    return "bg-slate-100 text-slate-800 ring-slate-200";
  }

  if (s === "submitted") {
    return "bg-sky-100 text-sky-800 ring-sky-200";
  }

  if (s === "approved") {
    return "bg-emerald-100 text-emerald-800 ring-emerald-200";
  }

  if (s === "rejected") {
    return "bg-rose-100 text-rose-800 ring-rose-200";
  }

  if (s === "paid") {
    return "bg-violet-100 text-violet-800 ring-violet-200";
  }

  return "bg-slate-100 text-slate-800 ring-slate-200";
};

const statusLabel = (statut) => {
  const labels = {
    draft: "Brouillon",
    submitted: "Soumise",
    approved: "Approuvée",
    rejected: "Rejetée",
    paid: "Payée",
  };

  return labels[statut] || statut || "Brouillon";
};

const pickItemsArray = (claim) => {
  if (!claim) return [];

  if (Array.isArray(claim.ClaimItems)) {
    return claim.ClaimItems;
  }

  if (Array.isArray(claim.items)) {
    return claim.items;
  }

  if (Array.isArray(claim.expense_items)) {
    return claim.expense_items;
  }

  return [];
};

/* ==========================================================================
   ANCIEN BAREME
   Uniquement pour les anciennes fiches.
   ========================================================================== */

const getLegacyIkCoef = (claim) => {
  if (claim?.moyen_transport !== "voiture") {
    return 0;
  }

  if (!claim?.covoiturage) {
    return 0.28;
  }

  if (claim?.covoiturage_role === "Passager") {
    return 0;
  }

  const passengers = Math.max(
    0,
    Math.floor(num(claim?.covoiturage_nb_passagers))
  );

  if (passengers === 0) return 0.28;
  if (passengers === 1) return 0.38;
  if (passengers === 2) return 0.48;

  return 0.58;
};

/* ==========================================================================
   DETECTION DU NOUVEAU CALCUL DETAILLE
   ========================================================================== */

function getMileageDetail(claim) {
  const detail = claim?.mileage_detail;

  if (
    !detail ||
    typeof detail !== "object" ||
    !detail.result ||
    typeof detail.result !== "object"
  ) {
    return null;
  }

  return detail;
}

/* ==========================================================================
   SOUS-COMPOSANT : DETAIL D'UN PARCOURS
   ========================================================================== */

function JourneySummary({ journey, title, variant = "blue" }) {
  if (!journey || !Array.isArray(journey.legs)) {
    return null;
  }

  const isReturn = variant === "violet";

  const headerClass = isReturn
    ? "bg-violet-50 text-violet-800"
    : "bg-blue-50 text-blue-800";

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div
        className={[
          "flex flex-wrap items-center justify-between gap-2 px-4 py-3",
          headerClass,
        ].join(" ")}
      >
        <div className="flex items-center gap-2 text-sm font-bold">
          <FaCar size={14} />

          {title}
        </div>

        <div className="text-xs font-semibold">
          {kmLabel(journey.distance_km)} km
          {" · "}
          {money(journey.montant_trajet)} €
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {journey.legs.map((leg, index) => (
          <div
            key={`${index}-${leg.origin}-${leg.destination}`}
            className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
          >
            <div className="min-w-0">
              <div className="break-words text-xs font-semibold text-slate-700">
                {leg.origin}
              </div>

              <div className="mt-1 flex items-start gap-2 text-xs text-slate-500">
                <FaArrowRight
                  size={10}
                  className="mt-1 shrink-0"
                />

                <span className="break-words">
                  {leg.destination}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span>
                  {kmLabel(leg.distance_km)} km
                </span>

                <span>•</span>

                <span>
                  {leg.passengers} passager(s)
                </span>

                <span>•</span>

                <span>
                  {rateLabel(leg.rate)} €/km
                </span>
              </div>
            </div>

            <div className="text-left sm:text-right">
              <div className="text-sm font-bold text-slate-800">
                {money(leg.amount)} €
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export default function ClaimSummaryCard({
  claimId,
  onEnsureClaim,
  onStatusChange,
}) {
  /* ========================================================================
     STATES
     ======================================================================== */

  const [claim, setClaim] = useState(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");

  /* ========================================================================
     LOAD
     ======================================================================== */

  const refresh = useCallback(async () => {
    if (!claimId) {
      setClaim(null);
      return;
    }

    try {
      setError("");
      setLoading(true);

      const data = await getExpenseClaimById(claimId);

      setClaim(data || null);
    } catch (e) {
      console.error("ClaimSummaryCard.refresh:", e);

      setError(
        e?.response?.data?.message ||
          "Impossible de charger le récapitulatif de la fiche."
      );

      setClaim(null);
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /* ========================================================================
     STATUS
     ======================================================================== */

  const canSubmit = useMemo(() => {
    return (
      !!claimId &&
      !!claim &&
      claim.statut === "draft" &&
      !loading &&
      !submitting
    );
  }, [claimId, claim, loading, submitting]);

  /* ========================================================================
     CALCULS
     ======================================================================== */

  const computed = useMemo(() => {
    if (!claim) {
      return {
        items: [],
        totalFrais: 0,
        totalGeneral: 0,
        trajet: 0,
        indTenue: 0,
        indRep: 0,
        isDetailed: false,
        mileageDetail: null,
        distanceAller: 0,
        distanceRetour: 0,
        distanceTotale: 0,
        coefIk: 0,
      };
    }

    /* ----------------------------------------------------------------------
       FRAIS ET INDEMNITES
       ---------------------------------------------------------------------- */

    const items = pickItemsArray(claim);

    const sumItems = items.reduce(
      (acc, item) => acc + num(item?.montant),
      0
    );

    const trajet = num(claim.montant_trajet);

    const indTenue = num(claim.indemnite_tenue);
    const indRep = num(claim.indemnite_representation);

    const serverTotalFrais = num(claim.total_frais);

    const totalFrais =
      serverTotalFrais > 0 || sumItems === 0
        ? serverTotalFrais
        : sumItems;

    const totalGeneralComputed =
      totalFrais + trajet + indTenue + indRep;

    const serverTotalGeneral = num(claim.total_general);

    const totalGeneral =
      serverTotalGeneral > 0 || totalGeneralComputed === 0
        ? serverTotalGeneral
        : totalGeneralComputed;

    /* ----------------------------------------------------------------------
       NOUVEAU CALCUL DETAILLE
       ---------------------------------------------------------------------- */

    const mileageDetail = getMileageDetail(claim);

    const isDetailed = !!mileageDetail;

    if (isDetailed) {
      const result = mileageDetail.result;

      return {
        items,
        totalFrais,
        totalGeneral,
        trajet,
        indTenue,
        indRep,

        isDetailed,
        mileageDetail,

        distanceAller: num(
          result?.aller?.distance_km
        ),

        distanceRetour: num(
          result?.retour?.distance_km
        ),

        // distance_km contient DEJA aller + retour.
        // Il ne faut surtout pas multiplier par deux.
        distanceTotale: num(claim.distance_km),

        // Taux moyen purement indicatif.
        coefIk: num(result.taux_moyen),
      };
    }

    /* ----------------------------------------------------------------------
       ANCIEN CALCUL : COMPATIBILITE HISTORIQUE
       ---------------------------------------------------------------------- */

    const distanceAller = num(claim.distance_km);

    const distanceTotale = claim.aller_retour
      ? distanceAller * 2
      : distanceAller;

    const coefIk = getLegacyIkCoef(claim);

    return {
      items,
      totalFrais,
      totalGeneral,
      trajet,
      indTenue,
      indRep,

      isDetailed: false,
      mileageDetail: null,

      distanceAller,
      distanceRetour: claim.aller_retour
        ? distanceAller
        : 0,

      distanceTotale,
      coefIk,
    };
  }, [claim]);

  /* ========================================================================
     SUBMIT
     ======================================================================== */

  const doSubmit = async () => {
    if (!canSubmit) return;

    const confirmed = window.confirm(
      "Soumettre la fiche ? Après soumission, la modification peut être bloquée."
    );

    if (!confirmed) return;

    try {
      setError("");
      setSubmitting(true);

      await submitExpenseClaim(claimId);

      await refresh();

      onStatusChange?.("submitted");
    } catch (e) {
      console.error("ClaimSummaryCard.submit:", e);

      setError(
        e?.response?.data?.message ||
          "Erreur lors de la soumission."
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ========================================================================
     JSX
     ======================================================================== */

  return (
    <SectionCard
      id="recap"
      title="Récapitulatif"
      subtitle="Vérifiez les montants enregistrés avant de soumettre votre fiche."
      icon={<FaClipboardCheck />}
      actions={
        <div className="flex flex-col gap-2">
          <Button
            text={loading ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={refresh}
            icon={FaSyncAlt}
            disabled={!claimId || loading || submitting}
          />

          <Button
            text={
              submitting
                ? "Soumission..."
                : "Soumettre"
            }
            variant="primary"
            size="sm"
            onClick={doSubmit}
            icon={FaPaperPlane}
            disabled={!canSubmit}
          />
        </div>
      }
    >
      {/* ================================================================
          AUCUNE FICHE
         ================================================================ */}

      {!claimId && (
        <div className="rounded-xl bg-amber-50 p-4 text-amber-900 ring-1 ring-amber-200">
          Enregistrez d'abord la fiche pour obtenir un identifiant.

          <div className="mt-3">
            <button
              type="button"
              className="h-10 rounded-xl bg-emerald-600 px-4 text-white transition hover:bg-emerald-700"
              onClick={onEnsureClaim}
            >
              Enregistrer maintenant
            </button>
          </div>
        </div>
      )}

      {/* ================================================================
          ERREUR
         ================================================================ */}

      {error && (
        <div
          role="alert"
          className="mt-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-800 ring-1 ring-rose-200"
        >
          {error}
        </div>
      )}

      {/* ================================================================
          RECAPITULATIF
         ================================================================ */}

      {claimId && (
        <div className="mt-4 rounded-2xl bg-white p-4 ring-1 ring-slate-200">
          {/* HEADER */}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-semibold text-slate-800">
              Fiche #{claimId}
            </div>

            {claim && (
              <span
                className={[
                  "inline-flex items-center rounded-full px-2.5 py-1 text-xs ring-1",
                  badge(claim.statut),
                ].join(" ")}
              >
                {statusLabel(claim.statut)}
              </span>
            )}
          </div>

          {/* LOADING */}

          {loading && (
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <FaSyncAlt className="animate-spin" />
              Actualisation du récapitulatif...
            </div>
          )}

          {/* DONNEES */}

          {claim && (
            <>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {/* FRAIS */}

                <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Total frais (lignes)
                  </div>

                  <div className="mt-1 text-lg font-semibold text-slate-900">
                    {money(computed.totalFrais)} €
                  </div>

                  <div className="mt-1 text-xs text-slate-500">
                    {computed.items.length} ligne(s)
                  </div>
                </div>

                {/* INDEMNITES */}

                <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Indemnités
                  </div>

                  <div className="mt-1 text-sm text-slate-700">
                    Tenue :{" "}
                    <strong>
                      {money(computed.indTenue)} €
                    </strong>
                  </div>

                  <div className="text-sm text-slate-700">
                    Représentation :{" "}
                    <strong>
                      {money(computed.indRep)} €
                    </strong>
                  </div>
                </div>

                {/* INDEMNITES KILOMETRIQUES */}

                <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                    <FaRoute className="text-indigo-500" />

                    Indemnités kilométriques
                  </div>

                  {/* Montant officiel enregistré en base */}

                  <div className="mt-1 text-lg font-semibold text-slate-900">
                    {money(computed.trajet)} €
                  </div>

                  {claim.moyen_transport === "voiture" ? (
                    computed.isDetailed ? (
                      /* NOUVELLE FICHE */

                      <div className="mt-3 space-y-2 text-xs text-slate-600">
                        <div className="flex flex-wrap justify-between gap-2">
                          <span>Distance aller</span>

                          <strong>
                            {kmLabel(computed.distanceAller)} km
                          </strong>
                        </div>

                        {computed.mileageDetail?.roundTrip && (
                          <div className="flex flex-wrap justify-between gap-2">
                            <span>Distance retour</span>

                            <strong>
                              {kmLabel(computed.distanceRetour)} km
                            </strong>
                          </div>
                        )}

                        <div className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-2">
                          <span>Distance totale parcourue</span>

                          <strong className="text-slate-900">
                            {kmLabel(computed.distanceTotale)} km
                          </strong>
                        </div>

                        <div className="flex flex-wrap justify-between gap-2">
                          <span>Taux kilométrique</span>

                          <strong className="text-indigo-700">
                            Variable selon les tronçons
                          </strong>
                        </div>

                        <div className="flex flex-wrap justify-between gap-2">
                          <span>Taux moyen pondéré</span>

                          <strong>
                            {rateLabel(computed.coefIk)} €/km
                          </strong>
                        </div>
                      </div>
                    ) : (
                      /* ANCIENNE FICHE */

                      <div className="mt-3 space-y-2 text-xs text-slate-600">
                        <div className="flex flex-wrap justify-between gap-2">
                          <span>Distance aller</span>

                          <strong>
                            {kmLabel(computed.distanceAller)} km
                          </strong>
                        </div>

                        <div className="flex flex-wrap justify-between gap-2">
                          <span>Distance indemnisée</span>

                          <strong>
                            {kmLabel(computed.distanceTotale)} km

                            {claim.aller_retour
                              ? " (aller-retour)"
                              : " (aller simple)"}
                          </strong>
                        </div>

                        <div className="flex flex-wrap justify-between gap-2">
                          <span>Coefficient kilométrique</span>

                          <strong>
                            {rateLabel(computed.coefIk)} €/km
                          </strong>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="mt-2 text-xs text-slate-500">
                      {claim.trajet_necessaire
                        ? `Transport : ${claim.moyen_transport || "Non précisé"}`
                        : "Aucun déplacement déclaré"}
                    </div>
                  )}
                </div>

                {/* TOTAL GENERAL */}

                <div className="rounded-xl bg-emerald-50 p-3 ring-1 ring-slate-200">
                  <div className="text-xs uppercase tracking-wide text-emerald-700">
                    Total général
                  </div>

                  <div className="mt-1 text-2xl font-extrabold text-emerald-900">
                    {money(computed.totalGeneral)} €
                  </div>

                  <div className="mt-2 text-xs text-emerald-700">
                    Frais + déplacement + indemnités
                  </div>
                </div>
              </div>

              {/* ========================================================
                  DETAIL DES TRONCONS MULTI-ETAPES
                 ======================================================== */}

              {computed.isDetailed &&
                computed.mileageDetail?.result && (
                  <div className="mt-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <FaRoute className="text-indigo-600" />

                      <h3 className="text-sm font-bold text-slate-800">
                        Détail du calcul kilométrique
                      </h3>
                    </div>

                    <p className="text-xs leading-5 text-slate-500">
                      Les indemnités sont calculées pour chaque
                      tronçon selon le nombre réel de passagers.
                    </p>

                    <JourneySummary
                      journey={
                        computed.mileageDetail.result.aller
                      }
                      title="Trajet aller"
                      variant="blue"
                    />

                    {computed.mileageDetail.roundTrip && (
                      <JourneySummary
                        journey={
                          computed.mileageDetail.result.retour
                        }
                        title="Trajet retour"
                        variant="violet"
                      />
                    )}
                  </div>
                )}

              {/* ========================================================
                  STATUT
                 ======================================================== */}

              {claim.statut !== "draft" && (
                <div className="mt-4 rounded-xl bg-sky-50 p-3 text-sm text-sky-800 ring-1 ring-sky-200">
                  Cette fiche n'est plus en brouillon :
                  certaines modifications peuvent être bloquées
                  côté serveur.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </SectionCard>
  );
}
